
import { useState, useEffect, useRef, useCallback } from 'react';
import { useTutorialContext } from '@/context/tutorial/TutorialContext';

interface UseTutorialManagerProps {
  pageId: string;
  showOnFirstVisit?: boolean;
}

// Keep track of the currently active tutorial globally
let activeTutorial: { id: string; close: () => void } | null = null;

export const useTutorialManager = ({ pageId, showOnFirstVisit = true }: UseTutorialManagerProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const { preferences, isLoading, dismissTutorial } = useTutorialContext();
  // Track if the tutorial has already been opened to avoid duplicate triggers
  const hasOpened = useRef(false);

  const closeTutorial = useCallback(() => {
    setIsOpen(false);
    dismissTutorial(pageId);
    if (activeTutorial?.id === pageId) {
      activeTutorial = null;
    }
  }, [dismissTutorial, pageId]);

  const closeAnyTutorial = () => {
    if (activeTutorial && activeTutorial.id !== pageId) {
      activeTutorial.close();
    }
  };

  const openTutorial = () => {
    if (preferences.globalEnabled) {
      closeAnyTutorial();
      activeTutorial = { id: pageId, close: closeTutorial };
      setIsOpen(true);
    }
  };

  // Check if this tutorial should be shown on first visit
  useEffect(() => {
    if (!isLoading && showOnFirstVisit && preferences.globalEnabled && !hasOpened.current) {
      const hasSeenTutorial = preferences.seenTutorials[pageId];

      if (hasSeenTutorial === undefined || hasSeenTutorial === false) {
        // Small delay to allow the page to render fully
        const timer = setTimeout(() => {
          closeAnyTutorial();
          openTutorial();
          hasOpened.current = true;
        }, 800);
        return () => clearTimeout(timer);
      }
    }
  }, [pageId, showOnFirstVisit, preferences.seenTutorials[pageId], preferences.globalEnabled, isLoading]);

  const isAnyTutorialOpen = activeTutorial !== null;

  return { isOpen, openTutorial, closeTutorial, isAnyTutorialOpen, closeAnyTutorial };
};

export default useTutorialManager;
